import { useEffect, useState } from 'react';
import { Braces, X } from 'lucide-react';
import { formatDate } from '../../lib/ctoMembers';
import SubscriberJsonModal from '../Subscribers/SubscriberJsonModal';
import { RepoMark } from './RepoMark';
import {
  BLAST_HINT,
  CATEGORY_HINT,
  CATEGORY_STYLE,
  EFFORT_HINT,
  type FindingStatus,
  type FindingWithContext,
} from '../../lib/ctoReviews';

const STATUS_ACTIONS: { status: FindingStatus; label: string }[] = [
  { status: 'open', label: 'Open' },
  { status: 'shipped', label: 'Close' },
  { status: 'declined', label: 'Decline' },
];

function statusLabel(status: FindingStatus): string {
  if (status === 'shipped') return 'Closed';
  if (status === 'fixed-since') return 'Fixed';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function Chip({
  label,
  className,
  hint,
}: {
  label: string;
  className?: string;
  hint?: string;
}) {
  return (
    <span
      title={hint}
      className={`text-[11px] px-1.5 py-0.5 rounded whitespace-nowrap ${
        hint ? 'cursor-help' : ''
      } ${className ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}
    >
      {label}
    </span>
  );
}

export function CtoFindingRow({
  finding,
  showSubject,
  onStatus,
}: {
  finding: FindingWithContext;
  showSubject?: boolean;
  onStatus: (status: FindingStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <li className="flex items-start gap-3 px-5 py-5 hover:bg-gray-50 dark:hover:bg-gray-700/40">
        <div
          className="shrink-0 w-14 pt-0.5"
          title={
            finding.effort
              ? EFFORT_HINT[finding.effort]
              : 'How long for one person with an assistant.'
          }
        >
          <div className="text-sm font-semibold leading-none text-gray-900 dark:text-white">
            {finding.effort ?? '—'}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-gray-400 mt-1">Effort</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {finding.title}
            </span>
            {finding.category && (
              <Chip
                label={finding.category}
                className={CATEGORY_STYLE[finding.category]}
                hint={CATEGORY_HINT[finding.category]}
              />
            )}
            {finding.blastRadius && (
              <Chip label={finding.blastRadius} hint={BLAST_HINT[finding.blastRadius]} />
            )}
            {!finding.staged && (
              <Chip
                label="set aside"
                hint="Seen and left off the short list. Still stored so the next run does not treat it as news."
              />
            )}
            {finding.status !== 'open' && <Chip label={statusLabel(finding.status)} />}
          </div>
          {finding.evidence && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{finding.evidence}</p>
          )}
          {finding.hinge && (
            <p className="text-xs text-gray-400 mt-1.5 italic">Changes if: {finding.hinge}</p>
          )}
          <div className="flex items-center gap-2 flex-wrap text-sm text-gray-500 dark:text-gray-400 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            {showSubject ? (
              <>
                <span>{finding.subject}</span>
                <span aria-hidden>·</span>
              </>
            ) : null}
            <RepoMark repo={finding.repo} />
            <span aria-hidden>·</span>
            <span>{formatDate(finding.reviewedAt)}</span>
            <span aria-hidden>·</span>
            <span className="font-mono">{finding.promptSlug ?? 'unattributed'}</span>
          </div>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-3 pt-0.5">
          <div
            className="w-12 text-right"
            title="Against their stage, not against perfect. Four and five are worth rearranging a week for."
          >
            <div className="text-lg font-semibold leading-none text-gray-900 dark:text-white tabular-nums">
              {finding.impact ?? '—'}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400 mt-1">Impact</div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700"
          >
            Action
          </button>
          <button
            type="button"
            onClick={() => setJsonOpen(true)}
            title="What we stored for this finding"
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-1 text-gray-500 hover:bg-white hover:text-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <Braces className="h-3.5 w-3.5" />
          </button>
        </div>
      </li>
      <SubscriberJsonModal
        isOpen={jsonOpen}
        onClose={() => setJsonOpen(false)}
        jsonString={JSON.stringify(finding, null, 2)}
      />

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby={`finding-${finding.id}-title`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="min-w-0 flex-1">
                <h2
                  id={`finding-${finding.id}-title`}
                  className="text-base font-semibold text-gray-900 dark:text-white"
                >
                  {finding.title}
                </h2>
                {finding.evidence ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{finding.evidence}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Now {statusLabel(finding.status).toLowerCase()}.
            </p>
            <div className="flex flex-wrap gap-2">
              {STATUS_ACTIONS.map((action) => {
                const active = finding.status === action.status;
                return (
                  <button
                    key={action.status}
                    type="button"
                    onClick={() => {
                      if (!active) onStatus(action.status);
                      setOpen(false);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                      active
                        ? 'bg-fuchsia-600 border-fuchsia-600 text-white'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
